import {
  HttpContextToken,
  HttpEvent,
  HttpEventType,
  HttpInterceptorFn,
  HttpResponse
} from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, finalize, map, of, tap } from 'rxjs';
import { CacheStorageService } from './cache-storage.service';
import { backendEndPointToken, fhirDefaultCountToken, patientBackendEndPointToken, terminologyCanonicalUrlBaseToken } from './app.config';
import { LoaderService } from './loader/loader.service';
import { AuthService } from './shared/auth/auth.service';
import { isHospitalAdmin } from './shared/system-roles';
export const LoadingUIEnabled = new HttpContextToken<boolean>(() => true);
export const DisableSortParam = new HttpContextToken<boolean>(() => false);
export const DisableCountParam = new HttpContextToken<boolean>(() => false);
export const DisableCache = new HttpContextToken<boolean>(() => false);
export const SkipHospitalSegregation = new HttpContextToken<boolean>(() => false);
export const SharedTenantContext = new HttpContextToken<readonly string[] | string | null>(() => null);

// ─── Extension URLs used in FHIR resources ───────────────────────────────────
const ACTOR_ORG_EXTENSION_URL = 'http://e-likita.org/fhir/StructureDefinition/actor-organization';
const LEGACY_HOSPITAL_ID_EXTENSION_URL = 'hospitalId'; // legacy short URL

// ─── Resource types that should SKIP org-based client-side filtering ─────────
// These are already filtered server-side by the gateway, or use participant/actor
// params that don't carry the hospitalId extension on the resource itself.
const SKIP_CLIENT_SIDE_ORG_FILTER = new Set([
  'Appointment',
  'AppointmentResponse',
  'Schedule',
  'Slot',
  'OperationOutcome', // always keep warnings/errors
]);

const TENANT_QUERY_SKIP_RESOURCE_TYPES = new Set([
  'CodeSystem',
  'Organization',
  'ValueSet',
  'StructureDefinition',
  'CapabilityStatement',
  'OperationDefinition',
  'MessageDefinition',
  'TerminologyCapabilities',
  'ConceptMap',
  'NamingSystem',
  'Questionnaire',
  'QuestionnaireResponse',
  'Binary',
  'Bundle',
  'metadata',
  'PractitionerRole',
  'Appointment',
  'AppointmentResponse'
]);

export const loadingInterceptor: HttpInterceptorFn = (req, next) => {
  const auth = inject(AuthService);
  const backendUrl = inject(backendEndPointToken);
  const patientBackendUrl = inject(patientBackendEndPointToken);
  const defaultCount = inject(fhirDefaultCountToken);
  const terminologyBaseUrl = inject(terminologyCanonicalUrlBaseToken);
  const tenantTagSystem = `${terminologyBaseUrl}/CodeSystem/organizationTenant`;
  const sharedTenantContextValue = req.context.get(SharedTenantContext);
  const sharedTenantOrgIds = normalizeSharedTenantIds(sharedTenantContextValue);
  const cacheStorage = inject(CacheStorageService);
  const loaderService = inject(LoaderService);

  const showLoader = req.context.get(LoadingUIEnabled);

  let clone = req.clone();

  const currentUser = auth.user.getValue();
  const loggedInPatientId = getLoggedInPatientId(currentUser);
  const loggedInPractitionerId = getLoggedInPractitionerId(currentUser);
  const loggedInUserOrganizationId = getLoggedInUserOrganizationId(currentUser);
  const loggedInOrganizationDisplay = getLoggedInOrganizationDisplay(currentUser);
  const patientBackendIsDistinct = !!patientBackendUrl && patientBackendUrl !== backendUrl;
  let routedToPatientBackend = false;
  let resourceType = extractFhirResourceTypeFromUrl(clone.url, backendUrl);
  let searchParams = tryGetSearchParams(clone.url);

  // Patient gateway routing: if the logged-in user is a Patient, route certain
  // patient-scoped reads to a patient-specific base URL.
   if (req.url.includes('/assets/')) {
    return next(req);
  }
  if (req.method === 'GET' && patientBackendIsDistinct && loggedInPatientId) {
    const patientParam = searchParams?.get('patient') || searchParams?.get('subject') || '';
    const requestedPatientId = normalizeIdFromReference('Patient', patientParam);

    const alwaysPatientBase =
      resourceType === 'ActivityDefinition' ||
      resourceType === 'CodeSystem' ||
      resourceType === 'Schedule' ||
      resourceType === 'Slot' ||
      resourceType === 'Appointment' ||
      resourceType === 'HealthcareService' ||
      resourceType === 'Task' ||
      resourceType === 'Location' ||
      resourceType === 'PractitionerRole' ||
      resourceType === 'Practitioner' ||
      resourceType === 'ValueSet' ||
      resourceType === 'ServiceRequest' ||
      resourceType === 'Observation' ||
      resourceType === 'ChargeItemDefinition' ||
      resourceType === 'ChargeItem' ||
      resourceType === 'Observation' ||
      resourceType === 'SupplyDelivery';
    const matchesExplicitPatient = !!requestedPatientId && requestedPatientId === loggedInPatientId;

    // Direct read of the logged-in patient's Patient/{id} should also go through the patient gateway.
    const requestedPatientIdFromPath = resourceType === 'Patient'
      ? normalizeIdFromReference('Patient', clone.url)
      : '';
    const matchesSelfPatientRead =
      resourceType === 'Patient' &&
      !!requestedPatientIdFromPath &&
      requestedPatientIdFromPath === loggedInPatientId;

    // If the request is explicitly patient-scoped (patient/subject matches), rewrite.
    // Also rewrite key scheduling resources for patient users (Schedule/Slot/Appointment/HealthcareService)
    // to ensure they consistently come from the patient gateway.
    if (matchesExplicitPatient || matchesSelfPatientRead || alwaysPatientBase) {
      const rewrittenUrl = replaceBaseUrl(clone.url, backendUrl, patientBackendUrl);
      if (rewrittenUrl !== clone.url) {
          if (shouldLogPatientGatewayRouting()) {
            console.log('[patient-gateway] rewrote', {
              resourceType,
              patientParam,
              requestedPatientId,
              loggedInPatientId,
              from: clone.url,
              to: rewrittenUrl
            });
          }
          clone = clone.clone({ url: rewrittenUrl });
          routedToPatientBackend = true;
          resourceType = extractFhirResourceTypeFromUrl(clone.url, backendUrl);
          searchParams = tryGetSearchParams(clone.url);
      }
    }
  }

  if (
    req.method === 'GET' &&
    patientBackendIsDistinct &&
    !loggedInPatientId &&
    !!loggedInPractitionerId &&
    resourceType === 'Appointment' &&
    !isUrlOnBase(clone.url, patientBackendUrl) &&
    shouldRoutePractitionerAppointmentToPatientBackend(searchParams, loggedInPractitionerId)
  ) {
    const rewrittenUrl = replaceBaseUrl(clone.url, backendUrl, patientBackendUrl);
    if (rewrittenUrl !== clone.url) {
        clone = clone.clone({ url: rewrittenUrl });
        routedToPatientBackend = true;
        resourceType = extractFhirResourceTypeFromUrl(clone.url, backendUrl);
        searchParams = tryGetSearchParams(clone.url);
    }
  }

  // Note: POST requests are no longer rewritten to the patient backend.
  // Previous logic would rewrite certain POSTs for labtechnician/alliedhealthprofessional roles
  // to the patient-specific backend. That behavior was removed to ensure all POSTs
  // go to the normal backend (backendUrl).

  if (
    req.method === 'GET' &&
    patientBackendIsDistinct &&
    (resourceType === 'ActivityDefinition' || resourceType === 'CodeSystem'
      || resourceType === 'ChargeItemDefinition' || resourceType === 'Schedule'
      || resourceType === 'ValueSet' || resourceType === 'Practitioner'
      || resourceType === 'Slot' || resourceType === 'ServiceRequest'
      || resourceType === 'Observation' || resourceType === 'Location'
      || resourceType === 'PractitionerRole' || resourceType === 'Task'
      || resourceType === 'SupplyDelivery' || resourceType === 'Appointment'
      || resourceType === 'HealthcareService') &&
    !isUrlOnBase(clone.url, patientBackendUrl)
  ) {
    const rewrittenUrl = replaceBaseUrl(clone.url, backendUrl, patientBackendUrl);
    if (rewrittenUrl !== clone.url) {
        clone = clone.clone({ url: rewrittenUrl });
        routedToPatientBackend = true;
    }
  }

  if (clone.method === 'GET' && resourceType === 'Appointment') {
    // const cleanedUrl = removeQueryParams(clone.url, ['_tag', '_security']);
    // if (cleanedUrl !== clone.url) {
    //   clone = clone.clone({ url: cleanedUrl });
    
      searchParams = tryGetSearchParams(clone.url);
    // }
  }

  // Patient gateway header: when a request targets the special patient backend,
  // include x-organization-id = logged-in user's organization id (as required by gateway).
  if (
    patientBackendIsDistinct &&
    loggedInUserOrganizationId &&
    (routedToPatientBackend || isUrlOnBase(clone.url, patientBackendUrl)) &&
    !clone.headers.has('x-organization-id')
  ) {
    clone = clone.clone({
      setHeaders: {
        'x-organization-id': loggedInUserOrganizationId
      }
    });
  }

  if (
    clone.method === 'GET' &&
    loggedInUserOrganizationId &&
    shouldApplyTenantTagFilter(
      clone.url,
      resourceType,
      backendUrl,
      patientBackendUrl,
      clone.context.get(SkipHospitalSegregation)
    )
  ) {
    if (!hasTenantTag(searchParams, tenantTagSystem, loggedInUserOrganizationId)) {
      clone = clone.clone({
        url: appendQueryParam(
          clone.url,
          '_tag',
          formatTenantQueryValue(tenantTagSystem, loggedInUserOrganizationId)
        )
      });
      searchParams = tryGetSearchParams(clone.url);
    }
  }

  if (
    clone.method === 'GET' &&
    sharedTenantOrgIds.length &&
    shouldApplyTenantTagFilter(
      clone.url,
      resourceType,
      backendUrl,
      patientBackendUrl,
      clone.context.get(SkipHospitalSegregation)
    )
  ) {
    if (!hasTenantSecurity(searchParams, tenantTagSystem, sharedTenantOrgIds)) {
      clone = clone.clone({
        url: appendQueryParam(
          clone.url,
          '_security',
          formatTenantSecurityQueryValue(tenantTagSystem, sharedTenantOrgIds)
        )
      });
      searchParams = tryGetSearchParams(clone.url);
    }
  }

  // Cache only idempotent GET requests (after any URL rewrite).
  if (
    clone.method === 'GET' &&
    !clone.context.get(DisableCache) &&
    cacheStorage.cache.hasOwnProperty(clone.urlWithParams)
  ) {
    console.log('[cache] HIT for', clone.urlWithParams);
    const cached = cacheStorage.cache[clone.urlWithParams];
    return of(new HttpResponse({ ...cached }));
  }

  if (showLoader) {
    loaderService.openLoader('Loading data from server...');
  }

  // Add _count param for FHIR list requests
  if (
    clone.method === 'GET' &&
    !clone.context.get(DisableCountParam) &&
    !clone.url.match('\/[a-zA-Z]{1,20}\/[a-zA-Z0-9\\-\\.]{1,64}$')
  ) {
    let modifiedUrl = clone.url
      .replace(/([?&])_count=\d+/g, '$1')
      .replace(/([?&])&+/g, '$1')
      .replace(/[?&]$/, '');
    const separator = modifiedUrl.includes('?') ? '&' : '?';
    modifiedUrl += `${separator}_count=${defaultCount}`;
    clone = clone.clone({ url: modifiedUrl });
  }

  // Add _sort param
  if (clone.method === 'GET' && !clone.context.get(DisableSortParam) && !clone.url.includes('_sort=')) {
    const separator = clone.url.includes('?') ? '&' : '?';
    clone = clone.clone({ url: clone.url + `${separator}_sort=-_lastUpdated` });
  }

  // Final handler: response filtering, caching, cache invalidation
  const finalizePipe = (c: typeof req) =>
    next(c).pipe(
      map(event => {
        // Hospital Segregation: Filter Response
        if (
          event instanceof HttpResponse &&
          c.method === 'GET' &&
          !c.context.get(SkipHospitalSegregation)
        ) {
          const responseParams = tryGetSearchParams(c.urlWithParams);

          if (
            resourceType === 'PractitionerRole' &&
            loggedInUserOrganizationId &&
            !hasExplicitOrganizationScope(responseParams)
          ) {
// $#$            /* alert('Received unscoped PractitionerRole bundle, applying client-side organization filter'); */
            return event.clone({
              body: filterPractitionerRoleBundleByOrganization(event.body, loggedInUserOrganizationId)
            });
          }

          // Skip filtering if the request already scopes to a specific patient/subject/participant/actor
          // — the server-side gateway already handled org isolation for these.
          const hasScopedParam =
            c.params.has('patient') ||
            c.params.has('subject') ||
            c.params.has('participant') ||
            c.params.has('actor');         // ✅ actor is the correct FHIR R4 Appointment param
          /*
                    if (!hasScopedParam) {
                      const user = auth.user.getValue();
                      const hId = user?.['hospitalId'] || user?.['organizationId'];
                      const body = event.body as any;
          
                      if (hId && body?.resourceType === 'Bundle') {
                        const entries = (body.entry || []).filter((e: any) => {
                          const res = e.resource;
                          if (!res) return true; // keep non-resource entries (e.g. search metadata)
          
                          // Always keep OperationOutcome entries (warnings, errors from server)
                          if (res.resourceType === 'OperationOutcome') return true;
          
                          // Skip client-side org filtering for resource types that don't carry
                          // the hospitalId extension (server gateway already filtered these).
                          if (SKIP_CLIENT_SIDE_ORG_FILTER.has(res.resourceType)) return true;
          
                          // Check both the legacy 'hospitalId' short URL and the canonical organization tenant URL
                          const ext = res.extension?.find(
                            (ex: any) =>
                              ex.url === organizationTenantExtensionUrl ||
                              ex.url === LEGACY_HOSPITAL_ID_EXTENSION_URL ||
                              ex.url === ACTOR_ORG_EXTENSION_URL
                          );
                          const resHId = ext?.valueReference?.reference?.split('/').pop();
          
                          return resHId === hId;
                        });
          
                        return event.clone({ body: { ...body, entry: entries } });
                      }
                    }*/
        }
        return event;
      }),
      tap((event: HttpEvent<any>) => {
        if (event.type === HttpEventType.Response) {
          console.log('[network] response for', c.method, c.urlWithParams);

          if (c.method === 'GET' && !c.context.get(DisableCache)) {
            cacheStorage.cache[c.urlWithParams] = event;
          } else {
            // Invalidate related cache entries on mutation
            const baseKey = c.url.split('?')[0];
            const mutatedResourceType = inferResourceTypeFromBases(baseKey, backendUrl, patientBackendUrl);
            const collectionPrefixes = mutatedResourceType
              ? buildCollectionPrefixes(mutatedResourceType, backendUrl, patientBackendUrl, baseKey)
              : [];

            Object.keys(cacheStorage.cache).forEach(k => {
              if (k.startsWith(baseKey)) {
                delete cacheStorage.cache[k];
                return;
              }
              if (collectionPrefixes.some(prefix => matchesCollectionCacheKey(k, prefix))) {
                delete cacheStorage.cache[k];
              }
            });
          }
        }
      }),
      catchError((e: any) => {
        throw e;
      }),
      finalize(() => {
        if (showLoader) {
          loaderService.closeLoader();
        }
      })
    );

  if (
    req.method === 'POST' &&
    clone.body &&
    loggedInUserOrganizationId &&
    !req.context.get(SkipHospitalSegregation)
  ) {
    const updatedBody = applyTenantMetadata(
      clone.body,
      tenantTagSystem,
      loggedInUserOrganizationId,
      loggedInOrganizationDisplay,
      sharedTenantOrgIds
    );
    clone = clone.clone({ body: updatedBody });
  }

  return finalizePipe(clone);
};

// Helper functions for hospital segregation
function applyTenantMetadata(
  body: any,
  tenantSystem: string,
  orgId: string,
  orgDisplay: string,
  sharedTenantOrgIds: string[]
): any {
  if (!body || typeof body !== 'object' || !orgId || !tenantSystem) {
    return body;
  }

  const resources = collectResourcesForTenantAnnotation(body);
  if (!resources.length) {
    return body;
  }

  const sanitizedDisplay = (orgDisplay || '').toString().trim();
  const uniqueSharedOrgIds = sharedTenantOrgIds.filter(id => id && id !== orgId);

  resources.forEach(resource => {
    if (!resource || typeof resource !== 'object' || !resource.resourceType) {
      return;
    }

    upsertMetaCoding(resource, 'tag', {
      system: tenantSystem,
      code: orgId,
      display: sanitizedDisplay || undefined
    });

    uniqueSharedOrgIds.forEach(sharedId => {
      upsertMetaCoding(resource, 'security', {
        system: tenantSystem,
        code: sharedId
      });
    });
  });

  return body;
}

function collectResourcesForTenantAnnotation(body: any): any[] {
  if (!body || typeof body !== 'object') {
    return [];
  }

  if (body.resourceType === 'Bundle') {
    return (body.entry || [])
      .filter((entry: any) => shouldAnnotateBundleEntry(entry))
      .map((entry: any) => entry.resource)
      .filter((res: any) => !!res);
  }

  if (body.resourceType) {
    return [body];
  }

  return [];
}

function shouldAnnotateBundleEntry(entry: any): boolean {
  if (!entry || !entry.resource) {
    return false;
  }
  const method = (entry.request?.method || 'POST').toString().toUpperCase();
  if (!entry.request) {
    return true;
  }
  return ['POST', 'PUT', 'PATCH'].includes(method);
}

interface TenantCoding {
  system: string;
  code: string;
  display?: string;
}

function upsertMetaCoding(target: any, collection: 'tag' | 'security', coding: TenantCoding): void {
  if (!target || !coding.code || !coding.system) {
    return;
  }

  target.meta = target.meta ?? {};
  const bucketKey = collection;
  const existingBucket = Array.isArray(target.meta[bucketKey]) ? target.meta[bucketKey] : [];
  if (!Array.isArray(target.meta[bucketKey])) {
    target.meta[bucketKey] = existingBucket;
  }

  const alreadyExists = existingBucket.find(
    (entry: any) => entry?.system === coding.system && entry?.code === coding.code
  );

  if (alreadyExists) {
    if (coding.display && !alreadyExists.display) {
      alreadyExists.display = coding.display;
    }
    return;
  }

  existingBucket.push({ ...coding });
}

function normalizeSharedTenantIds(value: readonly string[] | string | null): string[] {
  if (!value) return [];
  const values = Array.isArray(value) ? value : [value];
  return Array.from(
    new Set(
      values
        .map(v => normalizeIdFromReference('Organization', (v ?? '').toString().trim()))
        .filter((v): v is string => !!v)
    )
  );
}

function shouldApplyTenantTagFilter(
  url: string,
  resourceType: string,
  backendUrl: string,
  patientBackendUrl: string,
  skipSegregation: boolean
): boolean {
  if (skipSegregation) return false;
  if (!url || !resourceType) return false;
  if (!isFhirBackendRequest(url, backendUrl, patientBackendUrl)) return false;
  if (TENANT_QUERY_SKIP_RESOURCE_TYPES.has(resourceType)) return false;
  if (isMetadataLikePath(url)) return false;
  return true;
}

function isFhirBackendRequest(url: string, backendUrl: string, patientBackendUrl: string): boolean {
  return isUrlOnBase(url, backendUrl) || (!!patientBackendUrl && isUrlOnBase(url, patientBackendUrl));
}

function hasTenantTag(params: URLSearchParams | null, system: string, orgId: string): boolean {
  if (!params) return false;
  const taggedValues = params.getAll('_tag');
  return taggedValues.some(value => tagValueMatches(value, system, orgId));
}

function hasTenantSecurity(
  params: URLSearchParams | null,
  system: string,
  orgIds: string[]
): boolean {
  if (!params) return false;
  const values = params.getAll('_security');
  if (!values.length) return false;
  return orgIds.every(orgId => values.some(value => tagValueMatches(value, system, orgId)));
}

function tagValueMatches(value: string, system: string, orgId: string): boolean {
  if (!value) return false;
  return value.split(',').some(chunk => {
    const [candidateSystem, candidateCode] = chunk.split('|');
    return (
      decodeParamSegment(candidateSystem) === system &&
      decodeParamSegment(candidateCode) === orgId
    );
  });
}

function formatTenantQueryValue(system: string, orgId: string): string {
  return `${system}|${orgId}`;
}

function formatTenantSecurityQueryValue(system: string, orgIds: string[]): string {
  return orgIds
    .map(orgId => formatTenantQueryValue(system, orgId))
    .join(',');
}

function appendQueryParam(url: string, key: string, rawValue: string): string {
  if (!url) return url;
  const hasQuery = url.includes('?');
  const needsAmpersand = hasQuery && !url.endsWith('?') && !url.endsWith('&');
  const separator = hasQuery ? (needsAmpersand ? '&' : '') : '?';
  return `${url}${separator}${key}=${encodeURIComponent(rawValue)}`;
}

function removeQueryParams(url: string, keys: string[]): string {
  if (!url || !keys.length) return url;

  const [base, query = ''] = url.split('?');
  if (!query) {
    return url;
  }

  const params = new URLSearchParams(query);
  keys.forEach((key) => params.delete(key));
  const nextQuery = params.toString();
  return nextQuery ? `${base}?${nextQuery}` : base;
}

function decodeParamSegment(value?: string): string {
  if (!value) return '';
  try {
    return decodeURIComponent(value).trim();
  } catch {
    return value.trim();
  }
}

function isMetadataLikePath(url: string): boolean {
  const path = extractPathFromUrl(url);
  if (!path) return false;
  if (path.endsWith('/metadata')) return true;
  return /\/\$[A-Za-z]+/.test(path);
}

function extractPathFromUrl(raw: string): string {
  if (!raw) return '';
  const withoutQuery = raw.split('?')[0] || '';
  const protocolIndex = withoutQuery.indexOf('://');
  if (protocolIndex >= 0) {
    const pathIndex = withoutQuery.indexOf('/', protocolIndex + 3);
    return pathIndex >= 0 ? withoutQuery.substring(pathIndex) : '/';
  }
  // Relative URL
  return withoutQuery.startsWith('/') ? withoutQuery : `/${withoutQuery}`;
}

function isBareFhirBaseRequest(url: string, baseUrl: string): boolean {
  const raw = (url ?? '').toString().trim();
  const normalizedBase = normalizeBaseUrl(baseUrl);
  if (!raw || !normalizedBase) {
    return false;
  }

  return raw === normalizedBase || raw.startsWith(`${normalizedBase}?`);
}

function getLoggedInPatientId(user: any): string {
  if (!user) return '';
  const role = (user.role ?? '').toString().toLowerCase();
  const accountType = (user.accountType ?? user.userType ?? '').toString().toLowerCase();
  const roles: string[] = Array.isArray(user.roles) ? user.roles.map((r: any) => (r ?? '').toString().toLowerCase()) : [];

  const isPatient = role === 'patient' || accountType === 'patient' || roles.includes('patient');
  if (!isPatient) return '';

  // In this app, AuthService stores the underlying FHIR resource id as fhirResourceId.
  // For patient users this should be Patient/{id} or the raw id.
  const id = normalizeIdFromReference('Patient', user.fhirResourceId || user.fhirId || user.patientId || user.id);
  return id;
}

function getLoggedInPractitionerId(user: any): string {
  if (!user) return '';

  const role = (user.role ?? '').toString().toLowerCase();
  const accountType = (user.accountType ?? user.userType ?? '').toString().toLowerCase();
  const roles: string[] = Array.isArray(user.roles) ? user.roles.map((r: any) => (r ?? '').toString().toLowerCase()) : [];
  const isPatient = role === 'patient' || accountType === 'patient' || roles.includes('patient');

  if (isPatient) {
    return '';
  }

  return normalizeIdFromReference('Practitioner', user.fhirResourceId || user.fhirId || user.practitionerId || user.id);
}

function isAdminUser(user: any): boolean {
  if (!user) return false;

  const role = (user.role ?? '').toString().toLowerCase();
  const accountType = (user.accountType ?? user.userType ?? '').toString().toLowerCase();
  const roles: string[] = Array.isArray(user.roles) ? user.roles.map((r: any) => (r ?? '').toString().toLowerCase()) : [];

  return isHospitalAdmin(role) || accountType === 'admin' || roles.some(r => isHospitalAdmin(r));
}

function shouldRoutePractitionerAppointmentToPatientBackend(
  searchParams: URLSearchParams | null,
  practitionerId: string
): boolean {
  if (!searchParams || !practitionerId) {
    return false;
  }

  const actorValues = searchParams.getAll('actor');
  const participantValues = searchParams.getAll('participant');
  const expectedRefs = new Set([
    `Practitioner/${practitionerId}`,
    practitionerId
  ]);

  return [...actorValues, ...participantValues].some((value) => {
    const normalized = normalizeIdFromReference('Practitioner', value);
    return expectedRefs.has(value) || (!!normalized && expectedRefs.has(`Practitioner/${normalized}`));
  });
}

function shouldRouteRoleBasedPostToPatientBackend(
  user: any,
  resourceType: string,
  url: string,
  backendUrl: string
): boolean {
  if (user?.role !== 'labtechnician' && user?.role !== 'alliedhealthprofessional') {
    return false;
  }

  const normalizedResourceType = (resourceType || '').toString().toLowerCase();
  return normalizedResourceType === 'observation' || normalizedResourceType === 'activitydefinition' || isBareFhirBaseRequest(url, backendUrl);
}

function getLoggedInUserOrganizationId(user: any): string {
  if (!user) return '';

  // Prefer explicit org id from auth/session.
  const direct = user.organizationId || user.hospitalId || '';
  const normalizedDirect = normalizeIdFromReference('Organization', direct);
  if (normalizedDirect) return normalizedDirect;

  // Fallback: some flows store the chosen hospital/org id in localStorage.
  try {
    const stored = window.localStorage?.getItem('fhir_hospital_id') || '';
    const normalizedStored = normalizeIdFromReference('Organization', stored);
    if (normalizedStored) return normalizedStored;
  } catch {
    // ignore
  }

  return '';
}

function getLoggedInOrganizationDisplay(user: any): string {
  if (!user) return '';

  const directCandidates = [
    user.organizationName,
    user.organization?.name,
    user.organization?.display,
    user.organizationDisplay,
    user.hospitalName,
    user.hospital?.name,
    user.hospital?.display,
    user.hospitalDisplay
  ];

  for (const candidate of directCandidates) {
    if (typeof candidate === 'string' && candidate.trim()) {
      return candidate.trim();
    }
  }

  const storageKeys = ['selectedHospitalName', 'selectedOrganizationName', 'hospitalName'];
  const fromSession = readOrganizationNameFromStorage(storageKeys, () =>
    typeof window !== 'undefined' ? window.sessionStorage : null
  );
  if (fromSession) return fromSession;

  const fromLocal = readOrganizationNameFromStorage(storageKeys, () =>
    typeof window !== 'undefined' ? window.localStorage : null
  );
  if (fromLocal) return fromLocal;

  return '';
}

function readOrganizationNameFromStorage(
  keys: string[],
  storageFactory: () => Storage | null
): string {
  try {
    const storage = storageFactory();
    if (!storage) return '';
    for (const key of keys) {
      const value = storage.getItem(key);
      if (value && value.trim()) {
        return value.trim();
      }
    }
  } catch {
    // Ignore storage access issues (e.g., SSR or privacy mode).
  }
  return '';
}

function tryGetSearchParams(url: string): URLSearchParams | null {
  const raw = (url ?? '').toString();
  if (!raw) return null;

  // Fast path: if url contains '?', parse that part.
  const qIndex = raw.indexOf('?');
  if (qIndex >= 0) {
    return new URLSearchParams(raw.substring(qIndex + 1));
  }

  // If there is no query string, return empty params.
  return new URLSearchParams();
}

function replaceBaseUrl(url: string, fromBase: string, toBase: string): string {
  const raw = (url ?? '').toString();
  const from = (fromBase ?? '').replace(/\/$/, '');
  const to = (toBase ?? '').replace(/\/$/, '');
  if (!raw || !from || !to) return raw;
  if (raw.startsWith(from + '/')) {
    return to + raw.substring(from.length);
  }
  if (raw === from) {
    return to;
  }
  // If the request doesn't target the backend base, don't touch it.
  return raw;
}

function inferResourceTypeFromBases(url: string, backendUrl: string, patientBackendUrl: string): string {
  if (url.includes('/register-patient-direct') || url.includes('/register-patient')) {
    return 'Patient';
  }
  return (
    extractFhirResourceTypeFromUrl(url, backendUrl) ||
    (patientBackendUrl ? extractFhirResourceTypeFromUrl(url, patientBackendUrl) : '') ||
    extractResourceTypeFromRelativeUrl(url)
  );
}

function buildCollectionPrefixes(
  resourceType: string,
  backendUrl: string,
  patientBackendUrl: string,
  baseKey: string
): string[] {
  const prefixes = new Set<string>();
  if (resourceType) {
    const normalizedBackend = normalizeBaseUrl(backendUrl);
    if (normalizedBackend) {
      prefixes.add(`${normalizedBackend}/${resourceType}`);
    }

    const normalizedPatient = normalizeBaseUrl(patientBackendUrl);
    if (normalizedPatient) {
      prefixes.add(`${normalizedPatient}/${resourceType}`);
    }
  }

  const collectionFromUrl = extractCollectionPrefixFromUrl(baseKey);
  if (collectionFromUrl) {
    prefixes.add(collectionFromUrl);
  }

  return Array.from(prefixes).filter(Boolean);
}

function matchesCollectionCacheKey(cacheKey: string, collectionPrefix: string): boolean {
  if (!cacheKey || !collectionPrefix) {
    return false;
  }
  return cacheKey === collectionPrefix || cacheKey.startsWith(`${collectionPrefix}?`);
}

function normalizeBaseUrl(value: string): string {
  const trimmed = (value ?? '').toString().trim();
  if (!trimmed) return '';
  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
}

function extractCollectionPrefixFromUrl(url: string): string | null {
  if (!url) {
    return null;
  }
  try {
    const parsed = new URL(url, window.location.origin);
    const segments = parsed.pathname.split('/').filter(Boolean);
    if (segments.length >= 1) {
      const resourceIndex = segments.length - 2;
      if (resourceIndex >= 0) {
        const resource = segments[resourceIndex];
        const id = segments[resourceIndex + 1];
        if (resource && id) {
          const prefixPath = segments.slice(0, resourceIndex + 1).join('/');
          return `${parsed.origin}/${prefixPath}`;
        }
      }
    }
  } catch {
    const match = url.match(/^(.*\/[A-Za-z][A-Za-z0-9]+)\/[A-Za-z0-9\-.]+$/);
    if (match) {
      return match[1];
    }
  }
  return null;
}

function extractResourceTypeFromRelativeUrl(url: string): string {
  if (!url) return '';
  const match = url.match(/\/([A-Za-z][A-Za-z0-9]+)\/[A-Za-z0-9\-.]+$/);
  return match ? match[1] : '';
}

function extractFhirResourceTypeFromUrl(url: string, backendBase: string): string {
  const raw = (url ?? '').toString();
  if (!raw) return '';

  const base = (backendBase ?? '').replace(/\/$/, '');
  if (base && raw.startsWith(base)) {
    const after = raw.substring(base.length);
    const path = after.split('?')[0];
    const parts = path.split('/').filter(Boolean);
    return parts[0] || '';
  }

  // Fall back: best-effort parse for absolute/relative URLs.
  const path = raw.split('?')[0];
  const parts = path.split('/').filter(Boolean);
  return parts[parts.length - 2] || parts[parts.length - 1] || '';
}

function normalizeIdFromReference(resourceType: string, value: string | null | undefined): string {
  const input = (value ?? '').toString().trim();
  if (!input) return '';
  const withoutQuery = input.split('?')[0];
  const parts = withoutQuery.split('/').filter(Boolean);
  if (parts.length === 0) return '';
  const last = parts[parts.length - 1];
  if (parts.length >= 2) {
    const prev = parts[parts.length - 2];
    if (prev.toLowerCase() === resourceType.toLowerCase()) return last;
  }
  return last;
}

function shouldDebugPatientGatewayRouting(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage?.getItem('debug_patient_gateway') === '1';
  } catch {
    return false;
  }
}

function shouldLogPatientGatewayRouting(): boolean {
  try {
    return typeof window !== 'undefined' && window.localStorage?.getItem('log_patient_gateway') === '1';
  } catch {
    return false;
  }
}

function isUrlOnBase(url: string, baseUrl: string): boolean {
  const raw = (url ?? '').toString();
  const base = (baseUrl ?? '').toString().replace(/\/$/, '');
  if (!raw || !base) return false;
  return raw === base || raw.startsWith(base + '/');
}

function hasExplicitOrganizationScope(params: URLSearchParams | null): boolean {
  if (!params) return false;
  const organizationValues = params.getAll('organization');
  return organizationValues.some(value => !!normalizeIdFromReference('Organization', value));
}

function filterPractitionerRoleBundleByOrganization(body: any, organizationId: string): any {
  if (!body || body.resourceType !== 'Bundle' || !Array.isArray(body.entry) || !organizationId) {
    return body;
  }

  const filteredEntries = body.entry.filter((entry: any) => {
    const resource = entry?.resource;
    if (!resource) {
      return true;
    }

    if (entry?.search?.mode === 'outcome' || resource.resourceType === 'OperationOutcome') {
      return true;
    }

    if (resource.resourceType !== 'PractitionerRole') {
      return true;
    }

    const roleOrganizationId = normalizeIdFromReference('Organization', resource.organization?.reference);
    return !!roleOrganizationId && roleOrganizationId === organizationId;
  });

  return {
    ...body,
    entry: filteredEntries
  };
}